import {
	BasicButton,
	InputProfileGroups,
	InputTextArea,
	SecondCustomInput,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useUpdateGroup from "@/hooks/useUpdateGroup";
import { getGroupQueryOptions } from "@/queries";
import { UpdateGroupSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/chat/group/$id/edit")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const groupId = Route.useParams({
		select: (params) => params.id,
	});
	const { mutateAsync: mutateUpdateGroup } = useUpdateGroup();
	const { data: resGroup } = useSuspenseQuery(getGroupQueryOptions(groupId));
	const {
		control,
		register,
		watch,
		reset,
		trigger,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<UpdateGroupSchema>({
		mode: "all",
		resolver: zodResolver(UpdateGroupSchema),
	});
	const { avatar, directory } = watch();
	const profileUrl = useMemo(() => {
		if (!avatar) return undefined;
		if (avatar instanceof FileList) {
			if (!avatar.length) return undefined;
			// @ts-expect-error
			return URL.createObjectURL(avatar.item(0));
		}
		if (avatar instanceof File) return URL.createObjectURL(avatar);
		return avatar;
	}, [avatar]);
	const onSubmit: SubmitHandler<UpdateGroupSchema> = async (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		return mutateUpdateGroup(data, {
			onSuccess: (res) => {
				toast.success("Group profile updated");
				return navigate({
					to: "/dashboard/chat/group/$id/$channelName",
					params: { id: res.group.id, channelName: res.group.channel_name },
				});
			},
			onError: () => {
				toast.error("Failed to update group");
			},
		});
	};
	useEffect(() => {
		if (resGroup.group) {
			reset(
				{
					id: resGroup.group.id,
					avatar: resGroup.group.avatar,
					name: resGroup.group.name,
					organization: resGroup.group.organization ?? "",
					location: resGroup.group.location ?? "",
					synopsis: resGroup.group.synopsis ?? "",
					directory: resGroup.group.directory,
				},
				{
					keepDefaultValues: true,
				},
			);
			const triggerTimeout = setTimeout(() => {
				trigger();
			}, 500);
			return () => {
				clearTimeout(triggerTimeout);
			};
		}
	}, [reset, trigger, resGroup.group]);
	return (
		<div className="bg-white">
			<BackAppBar />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container relative flex flex-col gap-8"
			>
				<input type="hidden" {...register("id")} />
				<Typography fontSize={28} fontWeight={600}>
					Edit group profile
				</Typography>
				<div className="flex flex-col justify-center items-center gap-6">
					<InputProfileGroups image={profileUrl} {...register("avatar")} />
					<Controller
						control={control}
						name="name"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								type="text"
								label="Group Name*"
								name={field.name}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
					<Controller
						control={control}
						name="organization"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								type="text"
								label="Organization"
								name={field.name}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
					<Controller
						control={control}
						name="synopsis"
						defaultValue=""
						render={({ field, fieldState }) => (
							<InputTextArea
								fullWidth
								rows={4}
								maxLength={100}
								label="Group Synopsis"
								name={field.name}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								error={!!fieldState.error?.message}
							/>
						)}
					/>
					<Controller
						control={control}
						name="location"
						defaultValue=""
						render={({ field, fieldState }) => (
							<InputTextArea
								fullWidth
								rows={4}
								maxLength={100}
								label="Group Location Description"
								name={field.name}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								error={!!fieldState.error?.message}
							/>
						)}
					/>
					<div className="w-full">
						<p className="text-dark text-sm font-bold py-2">
							Add to HEMI Teams Directory
						</p>
						<Controller
							control={control}
							name="directory"
							render={({ field }) => (
								<div className="flex items-center my-2 gap-2">
									<BasicButton
										variant="outlined"
										color={field.value === true ? "info" : "inherit"}
										onClick={() => field.onChange(true)}
										onBlur={field.onBlur}
										className="w-full !h-12 !text-base font-medium !shadow-none"
									>
										<div className="flex items-center gap-2">
											<CheckIcon />
											<p>Yes</p>
										</div>
									</BasicButton>
									<BasicButton
										variant="outlined"
										color={field.value === false ? "error" : "inherit"}
										onClick={() => field.onChange(false)}
										onBlur={field.onBlur}
										className="w-full !h-12 !text-base font-medium !shadow-none"
									>
										<div className="flex items-center gap-2">
											<CloseIcon />
											<p>No</p>
										</div>
									</BasicButton>
								</div>
							)}
						/>
						<div className="flex items-start gap-1">
							<InfoRoundedIcon className="!text-base" />
							<p className="text-dark text-xs font-medium">
								{directory
									? "Your Group profile has been added to HEMI Teams Directory so others may see your Group directly"
									: "Your Group profile has not been added to the directory and other users who are not in your phone contact will not be able to see your Group."}
							</p>
						</div>
					</div>
				</div>
				<div className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<div className="wrapper">
						<div className="container">
							<BasicButton
								type="submit"
								variant="contained"
								color="secondary"
								disabled={!isDirty || !isValid}
								loading={isSubmitting}
								className="w-full !h-12 text-sm font-semibold !shadow-none"
							>
								Update
							</BasicButton>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
}
